import { Directive, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { NavHeaderRepository } from '@usana/ux/cms';
import { of } from 'rxjs';
import { UserService } from '../../modules/common/user.service';
import { IShopUser } from '../../modules/session/customer-models';
import { CmsStatus, CmsTypeGuard } from '../cms-type.guard';
import { DotcomTranslateService } from '../dotcom-translate/dotcom-translate.service';
import { LocaleUtil } from '../../modules/util/locale-util';
import { CmsNavData, CmsNavSection, DotcomMenuItem } from './dotcom-nav.models';

@Directive()
export abstract class DotcomNavBase implements OnInit, OnDestroy {

    menuItems: DotcomMenuItem[] = [];
    cmsNavData: CmsNavData | null = null;
    isCmsRoute = false;
    cmsLocale = 'en-us';

    protected teasLocale: string;
    protected usanaLocale: string;
    protected iso2Locale: string;
    protected userSub: Subscription;
    protected routerSub: Subscription;
    protected cmsNavSub: Subscription;
    /** True while any nav load (including the async loadMenuItems Promise chain) is in-flight. */
    protected isNavLoading = false;

    protected static readonly MENU_ITEM_COUNT = 6;
    static readonly SHOP_ITEM_NAME = 'Shop';

    isShopItem(label: string | undefined): boolean {
        return (label ?? '').trim().toLowerCase() === DotcomNavBase.SHOP_ITEM_NAME.toLowerCase();
    }

    isShopUrl(url: string | undefined): boolean {
        const u = (url ?? '').toLowerCase();
        return u.includes('/ux/cart') && u.includes('/landing');
    }

    /**
     * Returns true if a CMS nav section should render as the Shop dropdown.
     * Checks by header title, link label, and link URL to cover all locales —
     * non-English markets may have a translated label (e.g. "TIENDA") rather than "Shop".
     *
     * Note: Contentstack also stores a "Link Name (Internal Name)" field on each
     * modular block that is always "Shop" regardless of locale. This is not currently
     * mapped in shared-ux but could be used here in the future if needed.
     */
    isShopSection(section: CmsNavSection): boolean {
        return this.isShopItem(section.header?.headerTitle)
            || this.isShopItem(section.link?.label)
            || this.isShopUrl(section.link?.link?.href);
    }

    constructor(
        protected dotcomTranslateService: DotcomTranslateService,
        protected userService: UserService,
        protected cmsTypeGuard: CmsTypeGuard,
        protected router: Router,
        protected navHeaderRepository: NavHeaderRepository,
    ) {}

    ngOnInit(): void {
        this.userSub = this.userService.getShopUser().subscribe((user: IShopUser) => {
            const newUrlLocale = LocaleUtil.getUrlLocale(user?.usanaLocale);
            const prevUrlLocale = LocaleUtil.getUrlLocale(this.usanaLocale);
            this.teasLocale = user?.teasLocale;
            this.usanaLocale = user?.usanaLocale;
            this.iso2Locale = user?.iso2Locale;

            const localeChanged = newUrlLocale !== prevUrlLocale;
            // Don't clear here — keep showing the old nav while new data loads so there
            // is no blank flash. loadNavData() swaps in new data atomically on arrival.
            if (localeChanged || (!this.cmsNavData && !this.menuItems.length && !this.isNavLoading)) {
                this.loadNavData();
            }
        });

        this.routerSub = this.router.events.pipe(
            filter(e => e instanceof NavigationEnd),
        ).subscribe(() => {
            if (!this.cmsNavData && !this.menuItems.length && !this.isNavLoading && this.usanaLocale) {
                this.loadNavData();
            }
        });
    }

    ngOnDestroy(): void {
        this.userSub?.unsubscribe();
        this.routerSub?.unsubscribe();
        this.cmsNavSub?.unsubscribe();
    }

    /** Convert ALL-CAPS strings (as sometimes stored in Contentstack) to Title Case. */
    formatNavLabel(label: string | undefined): string {
        if (!label) {
            return '';
        }
        if (label === label.toUpperCase() && /[A-Z]/.test(label)) {
            return label.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
        }
        return label;
    }

    /**
     * Uses the guard's locale-exact Contentstack probe (with its session cache) to
     * decide CMS vs non-CMS, then loads the appropriate nav data.
     * For CMS locales the guard's cache makes the cmsStatus() call synchronous after
     * the first navigation, so there is no extra network round-trip.
     *
     * On Contentstack error the nav is left blank; the next navigation retries
     * automatically since errors are not cached by the guard.
     */
    protected loadNavData(): void {
        this.cmsNavSub?.unsubscribe();
        this.cmsLocale = LocaleUtil.getUrlLocale(this.iso2Locale) ?? 'en-US';
        // Mark loading so concurrent NavigationEnd events don't trigger a second load
        // while this one (or its downstream loadMenuItems Promise chain) is in-flight.
        this.isNavLoading = true;
        this.cmsNavSub = this.cmsTypeGuard.cmsStatus().pipe(
            take(1),
            switchMap((status: CmsStatus) => {
                if (status !== 'cms') {
                    return of({ status, navHeader: null });
                }
                return this.navHeaderRepository.getFirst(this.cmsLocale.toLowerCase()).pipe(
                    map((navHeader) => ({ status, navHeader })),
                );
            }),
        ).subscribe(({ status, navHeader }) => {
            if (status === 'cms' && navHeader) {
                this.isCmsRoute = true;
                this.cmsNavData = navHeader as unknown as CmsNavData;
                this.menuItems = [];
                this.isNavLoading = false;
            } else if (status === 'noncms' && this.teasLocale) {
                // Atomic swap: clear CMS nav and kick off translation-key load.
                // isNavLoading stays true until loadMenuItems() Promise chain settles.
                this.isCmsRoute = false;
                this.cmsNavData = null;
                this.loadMenuItems();
            } else {
                // Error or missing data: go blank (Contentstack will be retried on next navigation).
                this.isCmsRoute = false;
                this.cmsNavData = null;
                this.menuItems = [];
                this.isNavLoading = false;
            }
        });
    }

    protected loadMenuItems(): void {
        const count = DotcomNavBase.MENU_ITEM_COUNT;
        const promises = Array.from({ length: count }, (_, i) =>
            Promise.all([
                this.dotcomTranslateService.translate(`menu.${i}.url`, this.teasLocale),
                this.dotcomTranslateService.translate(`menu.${i}.name`, this.teasLocale),
                this.dotcomTranslateService.translate(`menu.${i}.target`, this.teasLocale),
            ]).then(([url, name, target]): DotcomMenuItem => ({
                url: url ?? '',
                name: name ?? '',
                target: target ?? '',
            }))
        );

        Promise.all(promises).then(items => {
            this.menuItems = items.filter(item => !!item.name?.trim() && !!item.url?.trim());
            this.isNavLoading = false;
        });
    }
}
